import { useState, useEffect, memo, useRef } from "react";
import axios from "axios";
import { API, useTheme } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup
} from "react-simple-maps";
import { Globe, MapPin, Flag, Plane, Check, X, Share2, Copy, ExternalLink, Download, Camera, Loader2, Lock, Crown, Sparkles, TrendingUp, Flame, Heart } from "lucide-react";
import html2canvas from "html2canvas";
import { shareToApp, shareViaWebAPI } from "@/hooks/useNativeShare";
import { useEntitlements, Features } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";
import { 
  RouteReplay, 
  YearlyGrowth, 
  MilestonePredictions, 
  PremiumInsights, 
  CityHeatmap,
  CoupleDashboard,
  JointHeatmap,
  SharedReplay,
  CompatibilityArchetypes
} from "@/components/premium";
import DuoAchievementsCard from "@/components/premium/DuoAchievementsCard";
import DuoFeedCard from "@/components/premium/DuoFeedCard";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

// Continent data with country codes and colors
const CONTINENTS = {
  'Africa': {
    color: '#F2994A',
    emoji: '🌍',
    countries: ['DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CM', 'CV', 'CF', 'TD', 'KM', 'CG', 'CD', 'DJ', 'EG', 'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'CI', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'TZ', 'TG', 'TN', 'UG', 'ZM', 'ZW']
  },
  'Asia': {
    color: '#EB5757',
    emoji: '🌏',
    countries: ['AF', 'AM', 'AZ', 'BH', 'BD', 'BT', 'BN', 'KH', 'CN', 'CY', 'GE', 'HK', 'IN', 'ID', 'IR', 'IQ', 'IL', 'JP', 'JO', 'KZ', 'KW', 'KG', 'LA', 'LB', 'MY', 'MV', 'MN', 'MM', 'NP', 'KP', 'OM', 'PK', 'PH', 'QA', 'SA', 'SG', 'KR', 'LK', 'SY', 'TW', 'TJ', 'TH', 'TL', 'TR', 'TM', 'AE', 'UZ', 'VN', 'YE']
  },
  'Europe': {
    color: '#2D9CDB',
    emoji: '🇪🇺',
    countries: ['AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'XK', 'LV', 'LI', 'LT', 'LU', 'MT', 'MD', 'MC', 'ME', 'NL', 'MK', 'NO', 'PL', 'PT', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'UA', 'GB', 'VA']
  },
  'North America': {
    color: '#9B51E0',
    emoji: '🌎',
    countries: ['AG', 'BS', 'BB', 'BZ', 'CA', 'CR', 'CU', 'DM', 'DO', 'SV', 'GD', 'GT', 'HT', 'HN', 'JM', 'MX', 'NI', 'PA', 'KN', 'LC', 'VC', 'TT', 'US']
  },
  'South America': {
    color: '#27AE60',
    emoji: '🌎',
    countries: ['AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE']
  },
  'Oceania': {
    color: '#F2C94C',
    emoji: '🌊',
    countries: ['AU', 'FJ', 'KI', 'MH', 'FM', 'NR', 'NZ', 'PW', 'PG', 'WS', 'SB', 'TO', 'TV', 'VU']
  }
};

// Get continent for a country code
const getContinent = (countryCode) => {
  for (const [continent, data] of Object.entries(CONTINENTS)) {
    if (data.countries.includes(countryCode)) {
      return continent;
    }
  }
  return null;
};

// Vibrant color palette for visited countries
const VISITED_COLORS = [
  "#E07A5F", // Terracotta (primary)
  "#81B29A", // Sage green
  "#F2994A", // Orange
  "#9B51E0", // Purple
  "#2D9CDB", // Blue
  "#27AE60", // Green
  "#EB5757", // Red
  "#F2C94C", // Yellow
];

// Founder exclusive gold color palette for visited countries
const FOUNDER_VISITED_COLORS = [
  "#D4AF37", "#FFD700", "#C5A028", "#E6BE44", "#B8860B", "#DAA520", "#F0C420", "#CFAA3E",
];

// Community Leader theme (referral reward) — ocean/teal palette
const COMMUNITY_LEADER_COLORS = [
  "#0D9488", "#14B8A6", "#2DD4BF", "#5EEAD4", "#0F766E", "#115E59", "#134E4A", "#0E7490",
];

// Map skin definitions
const MAP_SKINS = {
  default: { name: "Classic", colors: null, planned: "#F2CC8F", label: "Default" },
  founder_gold: { name: "Founder's Gold", colors: FOUNDER_VISITED_COLORS, planned: "#B8860B", label: "Founder's Gold", exclusive: true },
  community_leader_theme: { name: "Ocean Teal", colors: COMMUNITY_LEADER_COLORS, planned: "#0E7490", label: "Community Leader" },
};

// Get a consistent color for each country based on its code
const getCountryColor = (code, isVisited, isPlanned, isDarkMode = false, activeSkin = "default") => {
  const skin = MAP_SKINS[activeSkin] || MAP_SKINS.default;
  if (isVisited) {
    const hash = code.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    const palette = skin.colors || VISITED_COLORS;
    return palette[hash % palette.length];
  }
  if (isPlanned) {
    return skin.planned || "#F2CC8F";
  }
  return isDarkMode ? "#374151" : "#E8E4D9";
};

// Numeric ID to ISO A2 mapping for world-atlas countries
const numericToISO = {
  '004': 'AF', // Afghanistan
  '008': 'AL', // Albania
  '010': 'AQ', // Antarctica
  '012': 'DZ', // Algeria
  '020': 'AD', // Andorra
  '024': 'AO', // Angola
  '031': 'AZ', // Azerbaijan
  '032': 'AR', // Argentina
  '036': 'AU', // Australia
  '040': 'AT', // Austria
  '044': 'BS', // Bahamas
  '048': 'BH', // Bahrain
  '050': 'BD', // Bangladesh
  '051': 'AM', // Armenia
  '056': 'BE', // Belgium
  '064': 'BT', // Bhutan
  '068': 'BO', // Bolivia
  '070': 'BA', // Bosnia
  '072': 'BW', // Botswana
  '076': 'BR', // Brazil
  '100': 'BG', // Bulgaria
  '104': 'MM', // Myanmar
  '116': 'KH', // Cambodia
  '120': 'CM', // Cameroon
  '124': 'CA', // Canada
  '144': 'LK', // Sri Lanka
  '152': 'CL', // Chile
  '156': 'CN', // China
  '170': 'CO', // Colombia
  '178': 'CG', // Congo
  '180': 'CD', // DR Congo
  '188': 'CR', // Costa Rica
  '191': 'HR', // Croatia
  '192': 'CU', // Cuba
  '196': 'CY', // Cyprus
  '203': 'CZ', // Czech Republic
  '208': 'DK', // Denmark
  '214': 'DO', // Dominican Republic
  '218': 'EC', // Ecuador
  '818': 'EG', // Egypt
  '222': 'SV', // El Salvador
  '231': 'ET', // Ethiopia
  '233': 'EE', // Estonia
  '242': 'FJ', // Fiji
  '246': 'FI', // Finland
  '250': 'FR', // France
  '268': 'GE', // Georgia
  '276': 'DE', // Germany
  '288': 'GH', // Ghana
  '300': 'GR', // Greece
  '304': 'GL', // Greenland
  '320': 'GT', // Guatemala
  '332': 'HT', // Haiti
  '340': 'HN', // Honduras
  '344': 'HK', // Hong Kong
  '348': 'HU', // Hungary
  '352': 'IS', // Iceland
  '356': 'IN', // India
  '360': 'ID', // Indonesia
  '364': 'IR', // Iran
  '368': 'IQ', // Iraq
  '372': 'IE', // Ireland
  '376': 'IL', // Israel
  '380': 'IT', // Italy
  '388': 'JM', // Jamaica
  '392': 'JP', // Japan
  '400': 'JO', // Jordan
  '398': 'KZ', // Kazakhstan
  '404': 'KE', // Kenya
  '408': 'KP', // North Korea
  '410': 'KR', // South Korea
  '414': 'KW', // Kuwait
  '418': 'LA', // Laos
  '422': 'LB', // Lebanon
  '428': 'LV', // Latvia
  '440': 'LT', // Lithuania
  '442': 'LU', // Luxembourg
  '458': 'MY', // Malaysia
  '462': 'MV', // Maldives
  '466': 'ML', // Mali
  '470': 'MT', // Malta
  '484': 'MX', // Mexico
  '496': 'MN', // Mongolia
  '498': 'MD', // Moldova
  '499': 'ME', // Montenegro
  '504': 'MA', // Morocco
  '508': 'MZ', // Mozambique
  '524': 'NP', // Nepal
  '528': 'NL', // Netherlands
  '554': 'NZ', // New Zealand
  '558': 'NI', // Nicaragua
  '566': 'NG', // Nigeria
  '578': 'NO', // Norway
  '586': 'PK', // Pakistan
  '591': 'PA', // Panama
  '598': 'PG', // Papua New Guinea
  '600': 'PY', // Paraguay
  '604': 'PE', // Peru
  '608': 'PH', // Philippines
  '616': 'PL', // Poland
  '620': 'PT', // Portugal
  '630': 'PR', // Puerto Rico
  '634': 'QA', // Qatar
  '642': 'RO', // Romania
  '643': 'RU', // Russia
  '646': 'RW', // Rwanda
  '682': 'SA', // Saudi Arabia
  '686': 'SN', // Senegal
  '688': 'RS', // Serbia
  '702': 'SG', // Singapore
  '703': 'SK', // Slovakia
  '705': 'SI', // Slovenia
  '710': 'ZA', // South Africa
  '724': 'ES', // Spain
  '728': 'SS', // South Sudan
  '729': 'SD', // Sudan
  '752': 'SE', // Sweden
  '756': 'CH', // Switzerland
  '760': 'SY', // Syria
  '764': 'TH', // Thailand
  '788': 'TN', // Tunisia
  '792': 'TR', // Turkey
  '800': 'UG', // Uganda
  '804': 'UA', // Ukraine
  '807': 'MK', // North Macedonia
  '784': 'AE', // UAE
  '826': 'GB', // United Kingdom
  '834': 'TZ', // Tanzania
  '840': 'US', // United States
  '858': 'UY', // Uruguay
  '860': 'UZ', // Uzbekistan
  '862': 'VE', // Venezuela
  '704': 'VN', // Vietnam
  '887': 'YE', // Yemen
  '894': 'ZM', // Zambia
  '716': 'ZW', // Zimbabwe
  // Additional African countries
  '562': 'NE', // Niger
  '854': 'BF', // Burkina Faso
  '108': 'BI', // Burundi
  '132': 'CV', // Cape Verde
  '140': 'CF', // Central African Republic
  '148': 'TD', // Chad
  '174': 'KM', // Comoros
  '384': 'CI', // Côte d'Ivoire (Ivory Coast)
  '262': 'DJ', // Djibouti
  '226': 'GQ', // Equatorial Guinea
  '232': 'ER', // Eritrea
  '266': 'GA', // Gabon
  '270': 'GM', // Gambia
  '324': 'GN', // Guinea
  '624': 'GW', // Guinea-Bissau
  '426': 'LS', // Lesotho
  '430': 'LR', // Liberia
  '434': 'LY', // Libya
  '450': 'MG', // Madagascar
  '454': 'MW', // Malawi
  '478': 'MR', // Mauritania
  '480': 'MU', // Mauritius
  '516': 'NA', // Namibia
  '732': 'EH', // Western Sahara
  '706': 'SO', // Somalia
  '748': 'SZ', // Eswatini (Swaziland)
  '768': 'TG', // Togo
  '204': 'BJ', // Benin
  '678': 'ST', // Sao Tome and Principe
  '690': 'SC', // Seychelles
  '694': 'SL', // Sierra Leone
  // Additional Asian countries
  '060': 'BM', // Bermuda
  '096': 'BN', // Brunei
  '626': 'TL', // Timor-Leste
  '417': 'KG', // Kyrgyzstan
  '762': 'TJ', // Tajikistan
  '795': 'TM', // Turkmenistan
  // Additional European countries
  '438': 'LI', // Liechtenstein
  '492': 'MC', // Monaco
  '674': 'SM', // San Marino
  '336': 'VA', // Vatican City
  // Caribbean and Central America
  '028': 'AG', // Antigua and Barbuda
  '052': 'BB', // Barbados
  '084': 'BZ', // Belize
  '212': 'DM', // Dominica
  '308': 'GD', // Grenada
  '328': 'GY', // Guyana
  '659': 'KN', // Saint Kitts and Nevis
  '662': 'LC', // Saint Lucia
  '670': 'VC', // Saint Vincent and the Grenadines
  '740': 'SR', // Suriname
  '780': 'TT', // Trinidad and Tobago
  // Pacific Islands
  '090': 'SB', // Solomon Islands
  '548': 'VU', // Vanuatu
  '882': 'WS', // Samoa
  '776': 'TO', // Tonga
  '296': 'KI', // Kiribati
  '520': 'NR', // Nauru
  '583': 'FM', // Micronesia
  '584': 'MH', // Marshall Islands
  '585': 'PW', // Palau
  '798': 'TV', // Tuvalu
  // Middle East
  '512': 'OM', // Oman
};

const MapChart = memo(({ visitedCodes, plannedCodes, places, plannedPlaces, onCountryClick, isDarkMode, activeSkin = "default" }) => {
  // Center on Europe/Atlantic to see more countries clearly
  const [position, setPosition] = useState({ coordinates: [10, 35], zoom: 1.5 });

  const handleMoveEnd = (position) => {
    setPosition(position);
  };

  // Founders get gold outlines, regular users get white/dark
  const hasCustomSkin = activeSkin !== "default";
  const strokeColor = hasCustomSkin 
    ? "#D4AF37" // Gold outline for founders
    : isDarkMode ? "#1f2937" : "#FFFFFF";
  const hoverUnvisited = isDarkMode ? "#4B5563" : "#D5D0C4";
  
  // Founder-exclusive styling
  const strokeWidth = hasCustomSkin ? 0.8 : 0.5;
  const hoverStrokeWidth = hasCustomSkin ? 1.5 : 1;

  return (
    <ComposableMap
      projection="geoMercator"
      projectionConfig={{
        scale: 150,
      }}
      style={{ width: "100%", height: "100%", background: isDarkMode ? "#111827" : "transparent" }}
    >
      <ZoomableGroup
        zoom={position.zoom}
        center={position.coordinates}
        onMoveEnd={handleMoveEnd}
        minZoom={1}
        maxZoom={8}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              // Get country code from numeric ID first, then fallback to properties
              const numericId = String(geo.id).padStart(3, '0');
              let countryCode = numericToISO[numericId];
              
              // Fallback to ISO_A2 from properties if available
              if (!countryCode) {
                const isoA2 = geo.properties?.ISO_A2;
                if (isoA2 && isoA2 !== '-99') {
                  countryCode = isoA2;
                }
              }
              
              const isVisited = visitedCodes.includes(countryCode);
              const isPlanned = plannedCodes.includes(countryCode);
              const fillColor = getCountryColor(countryCode, isVisited, isPlanned, isDarkMode, activeSkin);
              
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onClick={() => onCountryClick(geo.properties.name, countryCode)}
                  style={{
                    default: {
                      fill: fillColor,
                      stroke: strokeColor,
                      strokeWidth: strokeWidth,
                      outline: "none",
                      transition: "all 0.2s ease",
                    },
                    hover: {
                      fill: isVisited ? (hasCustomSkin ? (MAP_SKINS[activeSkin]?.planned || "#264653") : "#264653") : isPlanned ? "#E5BC7F" : hoverUnvisited,
                      stroke: strokeColor,
                      strokeWidth: hoverStrokeWidth,
                      outline: "none",
                      cursor: "pointer",
                    },
                    pressed: {
                      fill: hasCustomSkin ? "#996515" : "#264653",
                      stroke: strokeColor,
                      strokeWidth: hoverStrokeWidth,
                      outline: "none",
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
        
        {/* Visited place markers */}
        {places.map((place) => (
          <Marker key={place.id} coordinates={[place.longitude, place.latitude]}>
            <circle r={5} fill="#FFFFFF" stroke="#264653" strokeWidth={2} />
            <circle r={2} fill="#264653" />
          </Marker>
        ))}
        
        {/* Planned place markers */}
        {plannedPlaces.map((place) => (
          <Marker key={place.id} coordinates={[place.longitude, place.latitude]}>
            <circle r={4} fill="#FFFFFF" stroke="#F2994A" strokeWidth={2} />
          </Marker>
        ))}
      </ZoomableGroup>
    </ComposableMap>
  );
});

export const WorldMap = ({ isOpen, onClose, onAddItem, user, dataVersion = 0 }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const isFoundingMember = user?.founding_explorer === true;
  const [useGoldTheme, setUseGoldTheme] = useState(true); // Founders can toggle gold theme
  const [activeSkin, setActiveSkin] = useState("default");
  
  // Build available skins based on user perks
  const availableSkins = ["default"];
  if (isFoundingMember) availableSkins.push("founder_gold");
  if (user?.unlocked_map_skins?.includes("community_leader_theme")) availableSkins.push("community_leader_theme");
  
  // Set initial skin
  useEffect(() => {
    if (isFoundingMember && useGoldTheme) setActiveSkin("founder_gold");
    else if (user?.unlocked_map_skins?.includes("community_leader_theme") && !isFoundingMember) setActiveSkin("community_leader_theme");
    else setActiveSkin("default");
  }, [isFoundingMember, useGoldTheme, user?.unlocked_map_skins]);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showAddPrompt, setShowAddPrompt] = useState(null); // For unvisited country click
  const [activeMapTab, setActiveMapTab] = useState('countries'); // 'countries' | 'continents' | 'insights' | 'duo'
  const [fullscreenTab, setFullscreenTab] = useState(null); // When set, shows fullscreen panel
  const [insightsKey, setInsightsKey] = useState(0); // Force remount of insight components on each panel open
  const [showShareModal, setShowShareModal] = useState(false); // Share modal
  const [copied, setCopied] = useState(false);
  const [mapImage, setMapImage] = useState(null); // Captured map image
  const [capturing, setCapturing] = useState(false); // Capturing state
  const mapContainerRef = useRef(null); // Ref for map container
  const [showPaywall, setShowPaywall] = useState(false);
  
  const { hasPaidAccess, canAccessFeature, entitlements, isFoundingMember: entitlementIsFounder } = useEntitlements();
  
  // Free users get basic map, premium unlocks heatmaps/themes/detailed stats
  const hasFullMapAccess = hasPaidAccess || isFoundingMember || entitlementIsFounder;

  const fetchMapData = async () => {
    setLoading(true);
    try {
      // Add cache-busting parameter to force fresh data
      const res = await axios.get(`${API}/map/visited?t=${Date.now()}`);
      setMapData(res.data);
    } catch (error) {
      console.error("Failed to fetch map data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setMapData(null); // Clear old data
      fetchMapData();
      setSelectedCountry(null);
      setActiveMapTab('countries'); // Reset to countries tab
      setInsightsKey(k => k + 1); // Force fresh data on next insights open
    }
  }, [isOpen]);
  
  // When trip data changes externally (create/edit/delete), bump insights key
  useEffect(() => {
    if (dataVersion > 0) {
      setInsightsKey(k => k + 1);
      if (isOpen) {
        fetchMapData(); // Also refresh map data if map is open
      }
    }
  }, [dataVersion]);

  // Calculate continent progress
  const getContinentProgress = () => {
    if (!mapData) return [];
    
    const visitedCodes = mapData?.visited?.country_codes || [];
    const plannedCodes = mapData?.planned?.country_codes || [];
    
    return Object.entries(CONTINENTS).map(([name, data]) => {
      const totalCountries = data.countries.length;
      const visitedInContinent = data.countries.filter(c => visitedCodes.includes(c)).length;
      const plannedInContinent = data.countries.filter(c => plannedCodes.includes(c)).length;
      const percentage = Math.round((visitedInContinent / totalCountries) * 100);
      
      return {
        name,
        emoji: data.emoji,
        color: data.color,
        total: totalCountries,
        visited: visitedInContinent,
        planned: plannedInContinent,
        percentage
      };
    }).sort((a, b) => b.percentage - a.percentage);
  };

  const continentProgress = getContinentProgress();

  const handleCountryClick = (name, code) => {
    // Skip if country code is invalid
    if (!code || code === 'undefined' || code === '-99') {
      toast.error(`Sorry, ${name} isn't available for tracking yet`);
      return;
    }
    
    const visited = mapData?.visited?.countries?.find(c => c.code === code);
    const planned = mapData?.planned?.countries?.find(c => c.code === code);
    
    if (visited || planned) {
      setSelectedCountry({ name, code, visited, planned });
      setShowAddPrompt(null);
    } else {
      // Show prompt to add this country
      setShowAddPrompt({ name, code });
      setSelectedCountry(null);
    }
  };

  const handleAddToBucketList = (asPastTrip = false) => {
    if (!showAddPrompt) return;
    
    // Close the map and trigger add item with country pre-filled
    onClose();
    if (onAddItem) {
      onAddItem({
        country: showAddPrompt.name,
        country_code: showAddPrompt.code,
        isPastTrip: asPastTrip,
      });
    }
    setShowAddPrompt(null);
  };

  const countriesVisited = mapData?.stats?.countries_visited || 0;
  const countriesPlanned = mapData?.stats?.countries_planned || 0;
  // Use same precision as ExplorerRank component (1 decimal place)
  const worldPercentage = Math.round((mapData?.stats?.world_percentage || 0) * 10) / 10;

  // Generate share text for social media
  const generateShareText = () => {
    const visitedList = mapData?.visited?.countries?.map(c => c.name).slice(0, 5).join(', ') || '';
    const moreCountries = countriesVisited > 5 ? ` +${countriesVisited - 5} more` : '';
    
    let text = `🌍 My World Map Progress:\n\n`;
    text += `✈️ ${countriesVisited} countries visited (${worldPercentage}% of the world!)\n`;
    text += `📍 ${countriesPlanned} destinations on my bucket list\n\n`;
    
    if (visitedList) {
      text += `Recent adventures: ${visitedList}${moreCountries}\n\n`;
    }
    
    // Add top continent
    if (continentProgress.length > 0 && continentProgress[0].percentage > 0) {
      text += `🏆 Best explored: ${continentProgress[0].emoji} ${continentProgress[0].name} (${continentProgress[0].percentage}%)\n\n`;
    }
    
    text += `Track your adventures at Duolyst.co.uk 🗺️`;
    
    return text;
  };

  const generateHashtags = () => {
    const tags = ['#duolyst', '#worldmap', '#travel', '#bucketlist', '#wanderlust', '#travelgoals'];
    
    // Add continent hashtags for visited continents
    continentProgress.forEach(c => {
      if (c.visited > 0) {
        tags.push(`#${c.name.replace(/\s+/g, '').toLowerCase()}`);
      }
    });
    
    return tags.slice(0, 10).join(' ');
  };

  const handleCopyCaption = async () => {
    const fullText = `${generateShareText()}\n\n${generateHashtags()}`;
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Caption copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const openInstagram = () => {
    handleCopyCaption();
    shareToApp("instagram", generateShareText() + "\n\n" + generateHashtags());
    toast.info("Caption copied! Paste it in your Instagram post");
  };

  const openTikTok = () => {
    handleCopyCaption();
    shareToApp("tiktok", generateShareText() + "\n\n" + generateHashtags());
    toast.info("Caption copied! Paste it in your TikTok post");
  };

  // Capture the map as an image
  const captureMapImage = async () => {
    if (!mapContainerRef.current) return;
    
    setCapturing(true);
    try {
      // Hide any overlays temporarily
      const overlays = mapContainerRef.current.querySelectorAll('.map-overlay');
      overlays.forEach(el => el.style.display = 'none');
      
      const canvas = await html2canvas(mapContainerRef.current, {
        backgroundColor: '#B8D4E3',
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
      });
      
      // Restore overlays
      overlays.forEach(el => el.style.display = '');
      
      const imageUrl = canvas.toDataURL('image/png');
      setMapImage(imageUrl);
      setShowShareModal(true);
    } catch (error) {
      console.error('Failed to capture map:', error);
      toast.error('Failed to capture map image');
      setShowShareModal(true); // Still show modal without image
    } finally {
      setCapturing(false);
    }
  };

  // Download the map image
  const downloadMapImage = () => {
    if (!mapImage) return;
    
    const link = document.createElement('a');
    link.download = `duolyst-world-map-${worldPercentage}percent.png`;
    link.href = mapImage;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Map image downloaded!');
  };

  // Native share using Web Share API (works on mobile)
  const handleNativeShare = async () => {
    if (!mapImage) {
      toast.error('Please wait for the map to load');
      return;
    }

    const caption = `${generateShareText()}\n\n${generateHashtags()}`;
    
    // Check if Web Share API is available with file sharing
    if (navigator.canShare) {
      try {
        // Convert base64 to blob
        const response = await fetch(mapImage);
        const blob = await response.blob();
        const file = new File([blob], `duolyst-map-${worldPercentage}percent.png`, { type: 'image/png' });
        
        const shareData = {
          title: 'My DuoLyst World Map',
          text: caption,
          files: [file]
        };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          toast.success('Shared successfully!');
          return;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
        }
      }
    }
    
    // Fallback: try sharing without file
    const shared = await shareViaWebAPI(caption, '', 'My DuoLyst World Map');
    if (shared) {
      toast.success('Caption shared! Download the image separately');
      return;
    }
    
    // Final fallback: download + copy
    downloadMapImage();
    handleCopyCaption();
    toast.info('Image downloaded & caption copied!');
  };

  // Handle share button click
  const handleShareClick = () => {
    captureMapImage();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!fixed !inset-0 !translate-x-0 !translate-y-0 !left-0 !top-0 !max-w-none !w-full !max-h-none !h-full !z-[10000] !flex !flex-col bg-card border-none rounded-none p-0 overflow-hidden" style={{transform: 'none', height: '100%', minHeight: '100dvh'}}>
        {/* Compact Header */}
        <DialogHeader className="p-4 pb-2 border-b border-border pr-12">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-serif text-lg text-foreground flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              World Map
            </DialogTitle>
            <DialogDescription className="sr-only">
              Interactive world map showing your visited and planned destinations
            </DialogDescription>
            
            {/* Actions & Stats in header */}
            {mapData && (
              <div className="flex items-center gap-2">
                {/* Share Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleShareClick}
                  disabled={capturing}
                  className="h-8 px-2"
                  data-testid="share-map-btn"
                >
                  {capturing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4" />
                  )}
                </Button>
                
                {/* Stats - hide percentage on very small screens to avoid overlap with X */}
                <span className="font-bold text-primary text-sm hidden xs:inline">{worldPercentage}%</span>
                <div className="hidden sm:flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                    <span>{countriesVisited} visited</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-accent" />
                    <span>{countriesPlanned} planned</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Map Theme Selector — shows when user has multiple skins */}
        {availableSkins.length > 1 && (
          <div className="flex justify-center gap-2 py-2 bg-card border-b border-border">
            {availableSkins.map(skinKey => {
              const skin = MAP_SKINS[skinKey];
              const isActive = activeSkin === skinKey;
              return (
                <button
                  key={skinKey}
                  onClick={() => { setActiveSkin(skinKey); if (skinKey === "founder_gold") setUseGoldTheme(true); else setUseGoldTheme(false); }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    isActive
                      ? skinKey === "founder_gold" ? "bg-gradient-to-r from-amber-600 to-yellow-500 text-white shadow-md"
                      : skinKey === "community_leader_theme" ? "bg-gradient-to-r from-teal-600 to-cyan-500 text-white shadow-md"
                      : "bg-primary text-white shadow-md"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {skin.label}
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-pulse text-primary text-lg font-serif">Loading your world map...</div>
          </div>
        ) : (
          <div className="relative flex flex-col flex-1 min-h-0">
            {/* Map takes full space */}
            <div ref={mapContainerRef} className={`flex-1 relative ${isDarkMode ? 'bg-gray-900' : 'bg-[#B8D4E3]'}`}>
              <MapChart
                visitedCodes={mapData?.visited?.country_codes || []}
                plannedCodes={mapData?.planned?.country_codes || []}
                places={mapData?.visited?.places || []}
                plannedPlaces={mapData?.planned?.places || []}
                onCountryClick={handleCountryClick}
                isDarkMode={isDarkMode}
                activeSkin={activeSkin}
              />
              
              {/* Legend - bottom left, just above floating tab bar */}
              <div className="absolute bottom-6 left-2 bg-card/95 backdrop-blur-sm rounded-lg p-2 shadow-lg text-xs">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: activeSkin === "founder_gold" ? "#D4AF37" : activeSkin === "community_leader_theme" ? "#0D9488" : "var(--primary)" }} />
                    <span className="text-foreground">Visited</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: MAP_SKINS[activeSkin]?.planned || "#F2CC8F" }} />
                    <span className="text-foreground">Planned</span>
                  </div>
                </div>
              </div>

              {/* Zoom hint - bottom right, just above floating tab bar */}
              <div className="absolute bottom-6 right-2 text-xs text-muted-foreground bg-card/80 px-2 py-1 rounded">
                Pinch to zoom • Drag to pan
              </div>

              {/* Add to bucket list prompt */}
              {showAddPrompt && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-20">
                  <div className="bg-card rounded-2xl shadow-2xl p-5 mx-4 max-w-sm w-full animate-in fade-in zoom-in duration-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-serif text-lg text-foreground flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-primary" />
                        {showAddPrompt.name}
                      </h3>
                      <button 
                        onClick={() => setShowAddPrompt(null)}
                        className="text-muted-foreground hover:text-foreground p-1"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add {showAddPrompt.name} to your bucket list
                    </p>
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => handleAddToBucketList(false)}
                        className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-11"
                      >
                        <Plane className="w-4 h-4 mr-2" />
                        Plan Future Trip
                      </Button>
                      <Button
                        onClick={() => handleAddToBucketList(true)}
                        variant="outline"
                        className="w-full rounded-xl h-11 border-secondary text-secondary hover:bg-secondary/10"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Log Past Visit
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile stats card - top */}
              <div className="sm:hidden absolute top-2 left-2 right-2">
                <div className="bg-card/95 backdrop-blur-sm rounded-xl p-3 shadow-lg flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold text-primary">{worldPercentage}%</div>
                    <div className="text-xs text-muted-foreground">explored</div>
                  </div>
                  <div className="flex gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-foreground">{countriesVisited}</div>
                      <div className="text-xs text-muted-foreground">visited</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-foreground">{countriesPlanned}</div>
                      <div className="text-xs text-muted-foreground">planned</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Country detail shown as overlay when a country is tapped */}
            {selectedCountry && !fullscreenTab && (
              <div className="absolute inset-0 z-[20] bg-card/95 backdrop-blur-sm overflow-y-auto">
                <div className="p-4 pb-20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-xl font-medium text-foreground">
                      {selectedCountry.name}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedCountry(null)}
                      data-testid="close-country-detail"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {selectedCountry.visited && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-secondary flex items-center gap-2 mb-2">
                        <Check className="w-4 h-4" />
                        Visited ({selectedCountry.visited.visits} times)
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selectedCountry.visited.items.map((item) => (
                          <div key={item.id} className="p-3 bg-secondary/10 rounded-lg">
                            <p className="font-medium text-foreground text-sm">{item.title}</p>
                            {item.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {item.location}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedCountry.planned && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-2 mb-2">
                        <Plane className="w-4 h-4" />
                        Planned ({selectedCountry.planned.items.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selectedCountry.planned.items.map((item) => (
                          <div key={item.id} className="p-3 bg-accent/20 rounded-lg">
                            <p className="font-medium text-foreground text-sm">{item.title}</p>
                            {item.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {item.location}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fullscreen tab overlays — use useEffect to disable parent dialog overlay on iOS */}
            {fullscreenTab && (
              <style>{`[data-state="open"].fixed.inset-0.z-50.bg-black\\/80 { pointer-events: none !important; }`}</style>
            )}
            {fullscreenTab === 'duo' ? (
              <div className="fixed inset-0 z-[60] bg-background flex flex-col animate-in slide-in-from-bottom duration-300">
                <div className="flex-shrink-0 bg-background/90 backdrop-blur-sm border-b border-border/50 px-4 py-3 flex items-center gap-3" style={{paddingTop: '12px'}}>
                  <Button variant="ghost" size="sm" onClick={() => setFullscreenTab(null)} data-testid="close-duo-panel">
                    <X className="w-4 h-4 mr-1" /> Back to Map
                  </Button>
                  <span className="text-sm font-medium text-foreground flex items-center gap-1.5"><Heart className="w-4 h-4 text-pink-500" /> Duo</span>
                </div>
                <div className="flex-1 overflow-y-auto" style={{WebkitOverflowScrolling: 'touch', paddingBottom: '16px'}}>
                  <div className="p-4 space-y-4 max-w-lg mx-auto">
                    <CoupleDashboard key={`duo-dash-${insightsKey}`} />
                    <DuoFeedCard key={`duo-feed-${insightsKey}`} />
                    <DuoAchievementsCard key={`duo-achievements-${insightsKey}`} />
                    <JointHeatmap key={`duo-heatmap-${insightsKey}`} />
                    <SharedReplay key={`duo-replay-${insightsKey}`} />
                    <CompatibilityArchetypes key={`duo-compat-${insightsKey}`} />
                  </div>
                </div>
              </div>
            ) : fullscreenTab === 'insights' ? (
              <div className="fixed inset-0 z-[60] bg-background flex flex-col animate-in slide-in-from-bottom duration-300">
                <div className="flex-shrink-0 bg-background/90 backdrop-blur-sm border-b border-border/50 px-4 py-3 flex items-center gap-3" style={{paddingTop: '12px'}}>
                  <Button variant="ghost" size="sm" onClick={() => setFullscreenTab(null)} data-testid="close-insights-panel">
                    <X className="w-4 h-4 mr-1" /> Back to Map
                  </Button>
                  <span className="text-sm font-medium text-foreground flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> Insights</span>
                </div>
                <div className="flex-1 overflow-y-auto" style={{WebkitOverflowScrolling: 'touch', paddingBottom: '16px'}}>
                  <div className="p-4 space-y-4 max-w-lg mx-auto">
                    <RouteReplay key={`replay-${insightsKey}`} onClose={() => setFullscreenTab(null)} onHighlightCountry={(code) => {}} />
                    <MilestonePredictions key={`milestones-${insightsKey}`} />
                    <PremiumInsights key={`insights-${insightsKey}`} />
                    <YearlyGrowth key={`growth-${insightsKey}`} />
                    <CityHeatmap key={`heatmap-${insightsKey}`} />
                  </div>
                </div>
              </div>
            ) : fullscreenTab === 'continents' ? (
              <div className="fixed inset-0 z-[60] bg-background flex flex-col animate-in slide-in-from-bottom duration-300">
                <div className="flex-shrink-0 bg-background/90 backdrop-blur-sm border-b border-border/50 px-4 py-3 flex items-center gap-3" style={{paddingTop: '12px'}}>
                  <Button variant="ghost" size="sm" onClick={() => setFullscreenTab(null)} data-testid="close-continents-panel">
                    <X className="w-4 h-4 mr-1" /> Back to Map
                  </Button>
                  <span className="text-sm font-medium text-foreground flex items-center gap-1.5"><Globe className="w-4 h-4 text-primary" /> Continents</span>
                </div>
                <div className="flex-1 overflow-y-auto" style={{WebkitOverflowScrolling: 'touch', paddingBottom: '16px'}}>
                  <div className="p-4 space-y-4 max-w-lg mx-auto">
                  <h4 className="text-sm font-medium text-foreground mb-3">Continent Progress</h4>
                  {continentProgress.map((continent) => (
                    <div key={continent.name} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{continent.emoji}</span>
                          <span className="font-medium text-foreground text-sm">{continent.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold" style={{ color: continent.color }}>{continent.percentage}%</span>
                          <span className="text-xs text-muted-foreground ml-1">({continent.visited}/{continent.total})</span>
                        </div>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${continent.percentage}%`, backgroundColor: continent.color }} />
                      </div>
                      {continent.planned > 0 && <p className="text-xs text-muted-foreground">+{continent.planned} planned</p>}
                    </div>
                  ))}
                  <div className="pt-4 border-t border-border mt-4">
                    <Button onClick={handleShareClick} disabled={capturing} className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white">
                      {capturing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                      {capturing ? 'Capturing...' : 'Share My Progress'}
                    </Button>
                  </div>
                </div>
                </div>
              </div>
            ) : fullscreenTab === 'countries' ? (
              <div className="fixed inset-0 z-[60] bg-background flex flex-col animate-in slide-in-from-bottom duration-300">
                <div className="flex-shrink-0 bg-background/90 backdrop-blur-sm border-b border-border/50 px-4 py-3 flex items-center gap-3" style={{paddingTop: '12px'}}>
                  <Button variant="ghost" size="sm" onClick={() => setFullscreenTab(null)} data-testid="close-countries-panel">
                    <X className="w-4 h-4 mr-1" /> Back to Map
                  </Button>
                  <span className="text-sm font-medium text-foreground flex items-center gap-1.5"><Flag className="w-4 h-4 text-primary" /> Countries</span>
                </div>
                <div className="flex-1 overflow-y-auto" style={{WebkitOverflowScrolling: 'touch', paddingBottom: '16px'}}>
                <div className="p-4 max-w-lg mx-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-2 mb-3">
                        <Check className="w-4 h-4 text-secondary" /> Visited ({countriesVisited})
                      </h4>
                      {mapData?.visited?.countries?.length > 0 ? (
                        <div className="space-y-1">
                          {mapData.visited.countries.map((country) => (
                            <button key={country.code} onClick={() => setSelectedCountry({ name: country.name, code: country.code, visited: country, planned: mapData?.planned?.countries?.find(c => c.code === country.code) })} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-secondary/10 transition-colors text-left">
                              <span className="text-sm text-foreground truncate">{country.name}</span>
                              <span className="text-xs text-muted-foreground ml-1">{country.visits}x</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">No countries visited yet</p>
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-2 mb-3">
                        <Plane className="w-4 h-4 text-primary" /> Bucket List ({countriesPlanned})
                      </h4>
                      {mapData?.planned?.countries?.length > 0 ? (
                        <div className="space-y-1">
                          {mapData.planned.countries.map((country) => (
                            <button key={country.code} onClick={() => setSelectedCountry({ name: country.name, code: country.code, visited: mapData?.visited?.countries?.find(c => c.code === country.code), planned: country })} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-accent/10 transition-colors text-left">
                              <span className="text-sm text-foreground truncate">{country.name}</span>
                              <span className="text-xs text-muted-foreground ml-1">{country.items.length}</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Add locations to your bucket list!</p>
                      )}
                    </div>
                  </div>
                </div>
                </div>
              </div>
            ) : null}

            {/* Sticky bottom tab bar — two-zone structure: interactive row + minimal safe-area filler */}
            {!selectedCountry && !fullscreenTab && (
              <div className="absolute left-0 right-0 z-[30] bottom-0">
                {/* Zone 1: Interactive tab row — distinct floating bar above the safe area */}
                <div className="mx-3 mb-14 p-1 bg-card/90 backdrop-blur-md rounded-2xl shadow-lg border border-border/30">
                  <div className="flex gap-1" data-testid="map-tab-bar">
                    <Button
                      variant={activeMapTab === 'countries' ? "default" : "ghost"}
                      size="sm"
                      onClick={() => { setActiveMapTab('countries'); setFullscreenTab('countries'); setInsightsKey(k => k + 1); }}
                      className="flex-1 text-xs min-w-0 h-8 rounded-xl gap-1.5 justify-center"
                      data-testid="tab-countries"
                    >
                      <Flag className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Countries</span>
                    </Button>
                    <Button
                      variant={activeMapTab === 'continents' ? "default" : "ghost"}
                      size="sm"
                      onClick={() => { setActiveMapTab('continents'); setFullscreenTab('continents'); setInsightsKey(k => k + 1); }}
                      className="flex-1 text-xs min-w-0 h-8 rounded-xl gap-1.5 justify-center"
                      data-testid="tab-continents"
                    >
                      <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Continents</span>
                    </Button>
                    <Button
                      variant={activeMapTab === 'insights' ? "default" : "ghost"}
                      size="sm"
                      onClick={() => { setActiveMapTab('insights'); setFullscreenTab('insights'); setInsightsKey(k => k + 1); }}
                      className="flex-1 text-xs min-w-0 h-8 rounded-xl gap-1.5 justify-center"
                      data-testid="tab-insights"
                    >
                      <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Insights</span>
                    </Button>
                    <Button
                      variant={activeMapTab === 'duo' ? "default" : "ghost"}
                      size="sm"
                      onClick={() => { setActiveMapTab('duo'); setFullscreenTab('duo'); setInsightsKey(k => k + 1); }}
                      className="flex-1 text-xs min-w-0 h-8 rounded-xl gap-1.5 justify-center"
                      data-testid="duo-tab-btn"
                    >
                      <Heart className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Duo</span>
                    </Button>
                  </div>
                </div>
                {/* Zone 2: Safe-area filler removed — native SafeAreaView handles bottom inset */}
              </div>
            )}

          </div>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-card rounded-2xl shadow-2xl p-5 max-w-md w-full animate-in fade-in zoom-in duration-200 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg text-foreground flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-primary" />
                  Share Your World Map
                </h3>
                <button 
                  onClick={() => {
                    setShowShareModal(false);
                    setMapImage(null);
                  }}
                  className="text-muted-foreground hover:text-foreground p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Map Image Preview */}
              {mapImage ? (
                <div className="mb-4">
                  <div className="relative rounded-xl overflow-hidden border border-border">
                    <img 
                      src={mapImage} 
                      alt="Your World Map" 
                      className="w-full h-auto"
                    />
                    {/* Overlay with stats */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <div className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          <span className="font-bold">{worldPercentage}% Explored</span>
                        </div>
                        <div className="text-sm">
                          {countriesVisited} countries
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Download Button */}
                  <Button
                    onClick={downloadMapImage}
                    variant="outline"
                    className="w-full mt-3 h-10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Map Image
                  </Button>
                </div>
              ) : (
                <div className="mb-4 p-8 bg-muted/50 rounded-xl flex flex-col items-center justify-center">
                  <Camera className="w-12 h-12 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Map image will appear here</p>
                </div>
              )}
              
              {/* Stats Summary */}
              <div className="p-3 bg-muted/50 rounded-xl mb-4">
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div>
                    <div className="text-xl font-bold text-primary">{worldPercentage}%</div>
                    <div className="text-xs text-muted-foreground">World</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-secondary">{countriesVisited}</div>
                    <div className="text-xs text-muted-foreground">Countries</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-foreground">{continentProgress.filter(c => c.visited > 0).length}</div>
                    <div className="text-xs text-muted-foreground">Continents</div>
                  </div>
                </div>
                
                {/* Top Continents */}
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-1.5">Continent Progress</p>
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {continentProgress.filter(c => c.visited > 0).slice(0, 6).map(c => (
                      <span key={c.name} className="text-xs bg-card px-2 py-0.5 rounded-full border border-border">
                        {c.emoji} {c.percentage}%
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Countries List */}
                {mapData?.visited?.countries?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">Countries Visited</p>
                    <div className="flex flex-wrap gap-1 justify-center max-h-20 overflow-y-auto">
                      {mapData.visited.countries.slice(0, 15).map(country => (
                        <span key={country.code} className="text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full">
                          {country.name}
                        </span>
                      ))}
                      {mapData.visited.countries.length > 15 && (
                        <span className="text-xs text-muted-foreground px-2 py-0.5">
                          +{mapData.visited.countries.length - 15} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Caption Preview - Collapsible */}
              <details className="mb-4">
                <summary className="cursor-pointer text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <Copy className="w-4 h-4" />
                  View Caption
                </summary>
                <div className="p-3 bg-background rounded-lg border border-border max-h-28 overflow-y-auto">
                  <p className="text-xs text-foreground whitespace-pre-line">{generateShareText()}</p>
                  <p className="text-xs text-primary mt-2">{generateHashtags()}</p>
                </div>
              </details>
              
              {/* Share Buttons */}
              <div className="space-y-2">
                {/* Primary Share Button - Uses Native Share on Mobile */}
                <Button
                  onClick={handleNativeShare}
                  disabled={!mapImage}
                  className="w-full h-12 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white text-base font-semibold"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Share Image + Caption
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  Opens your share menu - select Instagram, WhatsApp, etc.
                </p>
                
                {/* Alternative options */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => {
                      if (mapImage) downloadMapImage();
                      handleCopyCaption();
                      shareToApp("instagram", generateShareText() + "\n\n" + generateHashtags());
                    }}
                    variant="outline"
                    className="flex-1 h-10"
                  >
                    Instagram
                  </Button>
                  
                  <Button
                    onClick={() => {
                      if (mapImage) downloadMapImage();
                      handleCopyCaption();
                      shareToApp("tiktok", generateShareText() + "\n\n" + generateHashtags());
                    }}
                    variant="outline"
                    className="flex-1 h-10"
                  >
                    <TikTokIcon className="w-4 h-4 mr-1" />
                    TikTok
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleCopyCaption}
                    variant="outline"
                    className="flex-1 h-10"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-1 text-green-500" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Caption
                      </>
                    )}
                  </Button>
                  
                  {mapImage && (
                    <Button
                      onClick={downloadMapImage}
                      variant="outline"
                      className="flex-1 h-10"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Image
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
      
      {/* Premium Paywall */}
      <PaywallScreen
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        type={PaywallType.WORLD_MAP}
        triggerSource="world_map"
      />
    </Dialog>
  );
};

export default WorldMap;
